import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals `reinstateMembership` answers with.
 *
 * The fourth vocabulary of the app, and it exists for the same reason as the other
 * three: the code says which **gesture** unblocks the situation, and that is the only
 * thing the operator needs. The API's own sentences are notes written for a log:
 * half of them French, half English, one of them naming a role by its `code`, so the
 * wording here is ours. The reading of the code off the error is **not** copied: it
 * stays in `refusalCode`.
 *
 * Two of the eight are worth the care they get below.
 *
 * **Attention, `PORTAL_CLOSED_ON_ACCOUNT` is not about this person.** The account itself was
 * closed to the portal since the access was revoked, and reopening a membership on it
 * would write an access nobody can use. Nothing about the person is wrong, and no
 * amount of retrying changes it: somebody has to reopen the account first, which is a
 * back-office gesture of its own: hence a sentence that names the account rather than
 * the membership.
 *
 * **Attention, `PRIVILEGE_ESCALATION` is not « vous ne pouvez pas rouvrir ».** Reopening
 * restores the role the membership already carried, and restoring is granting: the
 * actor is refused because that **role** is beyond what they may grant, not because
 * the gesture is beyond them. Told the wrong way round, the operator asks for the
 * right to reopen (which nobody can grant them, there is no such right) instead of
 * handing the membership to somebody who carries the role. The refusal does not occur
 * with the roles in place today; it will the day a role outranks another.
 */
export const reinstateRefusalOf = (error: unknown): Refusal => {
    switch (refusalCode(error)) {
        case 'MEMBERSHIP_NOT_REVOKED':
            return {
                title: c('Reinstate refusal').t`This access is not closed.`,
                guidance: c('Reinstate refusal')
                    .t`There is nothing to reopen: it has been reopened since this page was loaded, or it never was closed. Reload the sheet to read its current state.`,
            };
        case 'PORTAL_CLOSED_ON_ACCOUNT':
            return {
                title: c('Reinstate refusal').t`This account is no longer open to the portal.`,
                guidance: c('Reinstate refusal')
                    .t`It was closed since this access was revoked, and reopening the access would grant an entry nobody can use. Nothing is wrong with this person: the account has to be opened to the portal again first, from its own sheet.`,
            };
        case 'ACCOUNT_DISABLED':
            return {
                title: c('Reinstate refusal').t`This person's account is disabled.`,
                guidance: c('Reinstate refusal')
                    .t`A disabled person is refused whatever their accesses say, so reopening one would change nothing on screen. Reactivating their account is a separate gesture, and it comes first.`,
            };
        case 'PRIVILEGE_ESCALATION':
            return {
                title: c('Reinstate refusal').t`You cannot grant the role this access carries.`,
                guidance: c('Reinstate refusal')
                    .t`Reopening restores the role as it was — the gesture takes no role of its own — and restoring a role is granting it. This one reaches beyond your own rights, so the refusal is about the role, not about reopening: hand the gesture to somebody who carries it, or have the role changed first.`,
            };
        case 'DIFFERENT_ROOT_ORGANIZATION':
            return {
                title: c('Reinstate refusal').t`This person now belongs to another client.`,
                guidance: c('Reinstate refusal')
                    .t`They joined a different client group while this access was closed, and a person belongs to a single one. Reopening here would attach them to two: their memberships on the other client have to be settled first.`,
            };
        case 'MEMBERSHIP_NOT_FOUND':
            return {
                title: c('Reinstate refusal').t`This access no longer exists.`,
                guidance: c('Reinstate refusal')
                    .t`It has disappeared since this page was opened. Go back to the list: the sheet you are reading is stale.`,
            };
        case 'MISSING_PERMISSION':
            // Attention, rendered after the click, and it cannot be otherwise: `viewer`
            // exposes the role but not the permissions behind it, so no screen can
            // know beforehand whether the actor holds `manage_users`.
            return {
                title: c('Reinstate refusal').t`You are not allowed to administer this person.`,
                guidance: c('Reinstate refusal')
                    .t`Reopening an access requires the right to manage the users of the account it belongs to, which your role does not carry. Ask for that right, or hand the gesture to whoever administers the account.`,
            };
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Reinstate refusal').t`This account is outside your portfolio.`,
                guidance: c('Reinstate refusal')
                    .t`The access belongs to an account that is not entrusted to you. Ask for that account to be added to your portfolio, or hand the gesture over.`,
            };
        default:
            return {
                title: c('Reinstate refusal').t`The access was not reopened.`,
                guidance: c('Reinstate refusal')
                    .t`This refusal is not one the screen knows how to read. Nothing has changed: the access is still closed exactly as it was. The server's answer is below — it is what the support desk needs.`,
                // Kept only when we could not read the refusal. An unknown code with
                // no detail leaves the operator with nothing to report.
                detail: error instanceof Error ? error.message : String(error),
            };
    }
};
